// Schema do banco (SQLite). Dados puros — nenhuma dependência de sql.js ou I/O.
// O adaptador de persistência lê `PRAGMA user_version` e aplica as migrações
// pendentes em ordem, dentro de uma transação, e grava a nova versão.

export interface Migration {
  /** Número da versão que esta migração leva o banco a atingir. Começa em 1. */
  readonly version: number;
  /** Uma linha por statement SQL, executados em ordem. */
  readonly statements: readonly string[];
}

export const migrations: readonly Migration[] = [
  {
    version: 1,
    statements: [
      `CREATE TABLE perfil (
        id    TEXT PRIMARY KEY,
        nome  TEXT NOT NULL
      )`,
      `CREATE TABLE usuario (
        id        TEXT PRIMARY KEY,
        nome      TEXT NOT NULL,
        login     TEXT,
        perfil_id TEXT REFERENCES perfil(id),
        ativo     INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE TABLE permissao_modulo (
        perfil_id TEXT NOT NULL REFERENCES perfil(id),
        modulo    TEXT NOT NULL,
        nivel     TEXT NOT NULL,
        PRIMARY KEY (perfil_id, modulo)
      )`,
    ],
  },
  {
    version: 2,
    statements: [
      `CREATE TABLE categoria (
        id    TEXT PRIMARY KEY,
        nome  TEXT NOT NULL,
        rota  TEXT NOT NULL DEFAULT '',
        ativo INTEGER NOT NULL DEFAULT 1
      )`,
    ],
  },
  {
    version: 3,
    statements: [
      `CREATE TABLE tipo_peca (
        id           TEXT PRIMARY KEY,
        nome         TEXT NOT NULL,
        categoria_id TEXT NOT NULL REFERENCES categoria(id),
        ativo        INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE TABLE tipo_peca_preco (
        tipo_id        TEXT NOT NULL REFERENCES tipo_peca(id),
        valor_centavos INTEGER NOT NULL,
        desde          TEXT NOT NULL,
        PRIMARY KEY (tipo_id, desde)
      )`,
    ],
  },
  {
    version: 4,
    statements: [
      `CREATE TABLE lista_valor (
        tipo  TEXT NOT NULL,
        valor TEXT NOT NULL,
        ordem INTEGER NOT NULL,
        PRIMARY KEY (tipo, valor)
      )`,
      `CREATE TABLE funcionario (
        id       TEXT PRIMARY KEY,
        nome     TEXT NOT NULL,
        funcao   TEXT NOT NULL DEFAULT '',
        contato  TEXT NOT NULL DEFAULT '',
        admissao TEXT,
        ativo    INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE TABLE insumo (
        id             TEXT PRIMARY KEY,
        nome           TEXT NOT NULL,
        categoria      TEXT NOT NULL DEFAULT '',
        unidade        TEXT NOT NULL DEFAULT '',
        estoque_minimo REAL NOT NULL DEFAULT 0,
        fornecedor     TEXT,
        ativo          INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE TABLE tempo_referencia (
        etapa TEXT PRIMARY KEY,
        horas REAL NOT NULL
      )`,
    ],
  },
  {
    version: 5,
    statements: [
      `CREATE TABLE cliente (
        id               TEXT PRIMARY KEY,
        nome             TEXT NOT NULL,
        endereco         TEXT NOT NULL DEFAULT '',
        contato          TEXT NOT NULL DEFAULT '',
        codigo           TEXT NOT NULL,
        tipo             TEXT NOT NULL DEFAULT '',
        base_faturamento TEXT NOT NULL DEFAULT 'saida',
        ciclo_tipo       TEXT NOT NULL DEFAULT 'mensal',
        ciclo_dia_inicio INTEGER,
        ciclo_dia_fim    INTEGER,
        ativo            INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE UNIQUE INDEX idx_cliente_codigo ON cliente(codigo)`,
    ],
  },
  {
    version: 6,
    statements: [
      `ALTER TABLE cliente ADD COLUMN sla_dias INTEGER`,
      `ALTER TABLE cliente ADD COLUMN sla_tipo TEXT`,
      `ALTER TABLE cliente ADD COLUMN sla_hora TEXT`,
      `CREATE TABLE fluxo_fixo (
        id             TEXT PRIMARY KEY,
        cliente_id     TEXT NOT NULL REFERENCES cliente(id),
        nome           TEXT NOT NULL,
        dias_semana    TEXT NOT NULL,
        horario_coleta TEXT NOT NULL,
        sla_dias       INTEGER,
        sla_tipo       TEXT,
        sla_hora       TEXT
      )`,
      `CREATE TABLE config (
        chave TEXT PRIMARY KEY,
        valor TEXT NOT NULL
      )`,
    ],
  },
  {
    version: 7,
    statements: [
      `CREATE TABLE preco_cliente (
        cliente_id     TEXT NOT NULL REFERENCES cliente(id),
        tipo_id        TEXT NOT NULL REFERENCES tipo_peca(id),
        valor_centavos INTEGER NOT NULL,
        desde          TEXT NOT NULL,
        PRIMARY KEY (cliente_id, tipo_id, desde)
      )`,
    ],
  },
  {
    // Tempo de referência passa a ser "N peças em M minutos" (produtividade),
    // em vez de só "M horas". Preserva o cadastro atual: horas → minutos,
    // pecas assume 1.
    version: 8,
    statements: [
      `CREATE TABLE tempo_referencia_new (
        etapa   TEXT PRIMARY KEY,
        pecas   REAL NOT NULL DEFAULT 1,
        minutos REAL NOT NULL
      )`,
      `INSERT INTO tempo_referencia_new (etapa, pecas, minutos)
        SELECT etapa, 1, horas * 60 FROM tempo_referencia`,
      `DROP TABLE tempo_referencia`,
      `ALTER TABLE tempo_referencia_new RENAME TO tempo_referencia`,
    ],
  },
  {
    // Redesenho da Fase 1 (ADR-0006): a rota sai da Categoria; o preço sai do
    // Tipo de Peça e vira coleção própria `preco_base`; o Tipo ganha `processos`.
    version: 9,
    statements: [
      `CREATE TABLE categoria_new (
        id    TEXT PRIMARY KEY,
        nome  TEXT NOT NULL,
        ativo INTEGER NOT NULL DEFAULT 1
      )`,
      `INSERT INTO categoria_new (id, nome, ativo)
        SELECT id, nome, ativo FROM categoria`,
      `DROP TABLE categoria`,
      `ALTER TABLE categoria_new RENAME TO categoria`,
      `ALTER TABLE tipo_peca ADD COLUMN processos TEXT NOT NULL DEFAULT ''`,
      `ALTER TABLE tipo_peca_preco RENAME TO preco_base`,
    ],
  },
  {
    // Lançar OS (ticket #9): o Lote e a Contagem Recebida por Tipo. A situação
    // é derivada — não há coluna `status` (ADR-0005).
    version: 10,
    statements: [
      `CREATE TABLE lote (
        id                  TEXT PRIMARY KEY,
        cliente_id          TEXT NOT NULL REFERENCES cliente(id),
        codigo              TEXT NOT NULL,
        os                  INTEGER NOT NULL,
        coleta_data         TEXT NOT NULL,
        coleta_hora         TEXT NOT NULL,
        fluxo_id            TEXT REFERENCES fluxo_fixo(id),
        sacos               INTEGER,
        sla_dias            INTEGER,
        sla_tipo            TEXT,
        sla_hora            TEXT,
        urgente             INTEGER NOT NULL DEFAULT 0,
        devolvido_em        TEXT,
        cancelado_em        TEXT,
        motivo_cancelamento TEXT
      )`,
      `CREATE INDEX idx_lote_cliente ON lote(cliente_id)`,
      `CREATE TABLE lote_contagem (
        lote_id  TEXT NOT NULL REFERENCES lote(id),
        tipo_id  TEXT NOT NULL REFERENCES tipo_peca(id),
        recebida INTEGER NOT NULL DEFAULT 0,
        PRIMARY KEY (lote_id, tipo_id)
      )`,
    ],
  },
  {
    // Registrar Devolução, Sublote e Backlog (ticket #27, ADR-0007).
    version: 11,
    statements: [
      `ALTER TABLE lote_contagem ADD COLUMN devolvida INTEGER NOT NULL DEFAULT 0`,
      // as retenções de um Lote **são** o Sublote dele
      `CREATE TABLE retencao (
        lote_id    TEXT NOT NULL REFERENCES lote(id),
        tipo_id    TEXT NOT NULL REFERENCES tipo_peca(id),
        motivo     TEXT NOT NULL,
        quantidade INTEGER NOT NULL,
        PRIMARY KEY (lote_id, tipo_id, motivo)
      )`,
      // peças de um Sublote que voltaram de carona noutra Devolução
      `CREATE TABLE baixa_backlog (
        lote_id        TEXT NOT NULL REFERENCES lote(id),
        origem_lote_id TEXT NOT NULL REFERENCES lote(id),
        tipo_id        TEXT NOT NULL REFERENCES tipo_peca(id),
        motivo         TEXT NOT NULL,
        quantidade     INTEGER NOT NULL,
        PRIMARY KEY (lote_id, origem_lote_id, tipo_id, motivo)
      )`,
      `CREATE INDEX idx_baixa_origem ON baixa_backlog(origem_lote_id)`,
    ],
  },
  {
    // Faturamento por Hotel (ticket #19). Fatura como agregado pequeno: os
    // ajustes, os lotes e as linhas congeladas ficam em colunas JSON, no mesmo
    // estilo da tabela `config`.
    version: 12,
    statements: [
      `CREATE TABLE fatura (
        id                TEXT PRIMARY KEY,
        cliente_id        TEXT NOT NULL REFERENCES cliente(id),
        periodo_de        TEXT NOT NULL,
        periodo_ate       TEXT NOT NULL,
        numero            TEXT,
        emitida_em        TEXT,
        pago              INTEGER NOT NULL DEFAULT 0,
        ajustes           TEXT NOT NULL DEFAULT '[]',
        lote_ids          TEXT NOT NULL DEFAULT '[]',
        linhas_congeladas TEXT NOT NULL DEFAULT '[]',
        reaberturas       TEXT NOT NULL DEFAULT '[]'
      )`,
      `CREATE INDEX idx_fatura_cliente ON fatura(cliente_id)`,
    ],
  },
  {
    version: 13,
    statements: [
      `ALTER TABLE cliente ADD COLUMN tipos_peca_visiveis TEXT`,
    ],
  },
  {
    version: 14,
    statements: [
      `ALTER TABLE usuario ADD COLUMN senha_hash TEXT`,
    ],
  },
  {
    version: 15,
    statements: [
      // Log de auditoria: append-only, gravado pelo servidor a cada escrita
      // bem-sucedida. Fora do Snapshot (não é regravado pelo writeSnapshot).
      `CREATE TABLE log_auditoria (
        id TEXT PRIMARY KEY,
        em TEXT NOT NULL,
        usuario_id TEXT,
        resumo TEXT NOT NULL,
        detalhe TEXT NOT NULL
      )`,
    ],
  },
  {
    // ADR-0013: a validação da regra de SLA passou a conferir o `tipo`, que
    // antes não era conferido por ninguém no domínio. A invariante nova vale
    // para os dados antigos também, então o que estiver fora de
    // ("corridos","uteis") é normalizado para "corridos" — o padrão do sistema.
    //
    // Só `lote.sla_tipo` tinha caminho de entrada para lixo (o cast de
    // `comandos/lotes.ts`); `cliente` e `fluxo_fixo` sempre passaram por `umDe`
    // na fronteira. As três são normalizadas assim mesmo: custa uma linha e
    // deixa a invariante valendo sem depender de a análise estar certa.
    //
    // `NULL` é um valor legítimo (não há regra naquele nível) e fica quieto.
    version: 16,
    statements: [
      `UPDATE cliente SET sla_tipo = 'corridos'
        WHERE sla_tipo IS NOT NULL AND sla_tipo NOT IN ('corridos', 'uteis')`,
      `UPDATE fluxo_fixo SET sla_tipo = 'corridos'
        WHERE sla_tipo IS NOT NULL AND sla_tipo NOT IN ('corridos', 'uteis')`,
      `UPDATE lote SET sla_tipo = 'corridos'
        WHERE sla_tipo IS NOT NULL AND sla_tipo NOT IN ('corridos', 'uteis')`,
      // O SLA padrão do sistema é JSON numa linha de `config`. Sem reescrever
      // JSON em SQL: a linha inválida é apagada e o carregador cai no default
      // semeado. `JSON.stringify` nunca separa a chave do valor, então o LIKE
      // não depende da ordem das chaves.
      `DELETE FROM config
        WHERE chave = 'slaPadrao'
          AND valor NOT LIKE '%"tipo":"corridos"%'
          AND valor NOT LIKE '%"tipo":"uteis"%'`,
    ],
  },
  {
    // Categoria Financeira (issue #56): classificação de Conta a Pagar/Receber.
    // `sistema` marca a que o usuário não pode editar nem remover — hoje só
    // "Faturamento", que a Fatura usa na Conta a Receber que gera (ADR-0011).
    version: 17,
    statements: [
      `CREATE TABLE categoria_financeira (
        id           TEXT PRIMARY KEY,
        nome         TEXT NOT NULL,
        subcategoria TEXT NOT NULL DEFAULT '',
        aplica_a     TEXT NOT NULL DEFAULT 'ambos',
        sistema      INTEGER NOT NULL DEFAULT 0,
        ativo        INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE UNIQUE INDEX idx_categoria_financeira_nome
        ON categoria_financeira(nome, subcategoria)`,
    ],
  },
  {
    // Dia de Vencimento (issue #40). No Cliente, os dias do mês em que a Fatura
    // vence, na ordem dos períodos do Ciclo — CSV porque o Ciclo quinzenal tem
    // dois. Vazio = não configurado, e a emissão abre o campo em branco.
    //
    // Na Fatura, o vencimento resolvido, gravado na emissão e ajustável à mão.
    version: 18,
    statements: [
      `ALTER TABLE cliente ADD COLUMN dias_vencimento TEXT NOT NULL DEFAULT ''`,
      `ALTER TABLE fatura ADD COLUMN vencimento TEXT`,
    ],
  },
  {
    // Contas a Pagar e a Receber (issue #16). As duas são a MESMA tabela,
    // separadas por `direcao` (ADR-0011). O Pagamento é entidade própria: uma
    // Conta tem vários, e é disso que a Situação de Pagamento deriva — por isso
    // não há coluna `pago` em lugar nenhum.
    version: 19,
    statements: [
      `CREATE TABLE conta (
        id             TEXT PRIMARY KEY,
        direcao        TEXT NOT NULL,
        descricao      TEXT NOT NULL,
        valor_centavos INTEGER NOT NULL,
        vencimento     TEXT NOT NULL,
        categoria_id   TEXT NOT NULL REFERENCES categoria_financeira(id),
        fonte          TEXT,
        cliente_id     TEXT REFERENCES cliente(id)
      )`,
      `CREATE INDEX idx_conta_direcao ON conta(direcao, vencimento)`,
      `CREATE TABLE pagamento (
        id             TEXT PRIMARY KEY,
        conta_id       TEXT NOT NULL REFERENCES conta(id),
        data           TEXT NOT NULL,
        valor_centavos INTEGER NOT NULL
      )`,
      `CREATE INDEX idx_pagamento_conta ON pagamento(conta_id)`,
    ],
  },
  {
    // Responsável e Retirada Pessoal (issue #17). A Retirada NÃO é uma Conta a
    // Pagar (ADR-0011): tabela própria, sem vencimento e sem pagamento, porque
    // ela soma à parte das despesas da empresa — que é o motivo do módulo.
    version: 20,
    statements: [
      `CREATE TABLE responsavel (
        id                     TEXT PRIMARY KEY,
        nome                   TEXT NOT NULL,
        limite_mensal_centavos INTEGER,
        ativo                  INTEGER NOT NULL DEFAULT 1
      )`,
      `CREATE TABLE retirada_pessoal (
        id             TEXT PRIMARY KEY,
        responsavel_id TEXT NOT NULL REFERENCES responsavel(id),
        data           TEXT NOT NULL,
        valor_centavos INTEGER NOT NULL
      )`,
      `CREATE INDEX idx_retirada_data ON retirada_pessoal(data)`,
    ],
  },
  {
    // Fatura emitida gera Conta a Receber (issue #41, ADR-0011).
    //
    // A Fatura perde o `pago`: quem tem Situação de Pagamento agora é a Conta.
    // Mas as Contas retroativas NÃO podem ser criadas aqui — o valor de uma
    // Fatura é **calculado** (`calcularFatura`, sobre linhas congeladas em
    // JSON e ajustes), e refazer essa soma em SQL seria uma segunda definição
    // do total, livre para divergir da primeira.
    //
    // Então a migração só preserva o fato "esta Fatura estava paga" numa
    // tabela-ponte, e quem cria as Contas é o domínio, no boot
    // (`garantirContasDeFaturas`), onde `calcularFatura` existe. A ponte é
    // consumida e apagada lá.
    version: 21,
    statements: [
      `ALTER TABLE conta ADD COLUMN fatura_id TEXT REFERENCES fatura(id)`,
      `CREATE INDEX idx_conta_fatura ON conta(fatura_id)`,
      `CREATE TABLE fatura_pago_legado (fatura_id TEXT PRIMARY KEY)`,
      `INSERT INTO fatura_pago_legado (fatura_id)
        SELECT id FROM fatura WHERE pago = 1 AND emitida_em IS NOT NULL`,
      // rebuild sem a coluna `pago` (mesmo padrão das migrações v8 e v9)
      `CREATE TABLE fatura_new (
        id                TEXT PRIMARY KEY,
        cliente_id        TEXT NOT NULL REFERENCES cliente(id),
        periodo_de        TEXT NOT NULL,
        periodo_ate       TEXT NOT NULL,
        numero            TEXT,
        emitida_em        TEXT,
        ajustes           TEXT NOT NULL DEFAULT '[]',
        lote_ids          TEXT NOT NULL DEFAULT '[]',
        linhas_congeladas TEXT NOT NULL DEFAULT '[]',
        reaberturas       TEXT NOT NULL DEFAULT '[]',
        vencimento        TEXT
      )`,
      `INSERT INTO fatura_new
        SELECT id, cliente_id, periodo_de, periodo_ate, numero, emitida_em,
               ajustes, lote_ids, linhas_congeladas, reaberturas, vencimento
          FROM fatura`,
      `DROP TABLE fatura`,
      `ALTER TABLE fatura_new RENAME TO fatura`,
      `CREATE INDEX idx_fatura_cliente ON fatura(cliente_id)`,
    ],
  },
  {
    // ADR-0014 — Fase 3, parte aditiva. Cria a tabela `parada` (Rotina de
    // Coleta e Devolução). `fluxo_fixo` e as colunas SLA continuam vivas até a
    // migração destrutiva da Fase D, que move os dados e remove o modelo velho.
    // As colunas novas de `cliente` e `lote` chegam nas migrações das tarefas
    // que as consomem (cada migração aditiva junto do seu mapeamento).
    version: 22,
    statements: [
      `CREATE TABLE parada (
        id          TEXT PRIMARY KEY,
        cliente_id  TEXT NOT NULL REFERENCES cliente(id),
        dias_semana TEXT NOT NULL,
        horario     TEXT NOT NULL,
        acao        TEXT NOT NULL
      )`,
    ],
  },
  {
    // ADR-0014 — Fase 3. Folga da devolução no Cliente (aditiva).
    version: 23,
    statements: [
      `ALTER TABLE cliente ADD COLUMN devolucao_no_mesmo_dia INTEGER NOT NULL DEFAULT 0`,
    ],
  },
  {
    // ADR-0014 — Fase 3. Campos novos de Lote (aditiva).
    version: 24,
    statements: [
      `ALTER TABLE lote ADD COLUMN parada_id TEXT REFERENCES parada(id)`,
      `ALTER TABLE lote ADD COLUMN previsto_manual TEXT`,
      `ALTER TABLE lote ADD COLUMN devolvido_hora TEXT`,
    ],
  },
  {
    // ADR-0014 — Fase 3, parte destrutiva. Move os Fluxos Fixos para Paradas de
    // coleta (as de devolução o dono cadastra na grade nova), dropa `fluxo_fixo`
    // e as colunas SLA, e copia `lote.fluxo_id` → `parada_id`. SQLite 3.49
    // (sql.js 1.14) suporta DROP COLUMN.
    version: 25,
    statements: [
      `INSERT INTO parada (id, cliente_id, dias_semana, horario, acao)
        SELECT id, cliente_id, dias_semana, horario_coleta, 'coletar' FROM fluxo_fixo`,
      `UPDATE lote SET parada_id = fluxo_id WHERE fluxo_id IS NOT NULL
        AND fluxo_id IN (SELECT id FROM parada)`,
      `ALTER TABLE lote DROP COLUMN fluxo_id`,
      `DROP TABLE fluxo_fixo`,
      `ALTER TABLE cliente DROP COLUMN sla_dias`,
      `ALTER TABLE cliente DROP COLUMN sla_tipo`,
      `ALTER TABLE cliente DROP COLUMN sla_hora`,
      `ALTER TABLE lote DROP COLUMN sla_dias`,
      `ALTER TABLE lote DROP COLUMN sla_tipo`,
      `ALTER TABLE lote DROP COLUMN sla_hora`,
      `DELETE FROM config WHERE chave = 'slaPadrao'`,
    ],
  },
  {
    // OS de relavagem — ADR-0015. Reprocesso pós-entrega não-cobrado.
    // `relavagem = 1` exclui o Lote do cálculo da Fatura e exibe em cinza
    // na Planilha de Fechamento. Aditiva — sem migração de dados necessária.
    version: 26,
    statements: [
      `ALTER TABLE lote ADD COLUMN relavagem INTEGER NOT NULL DEFAULT 0`,
    ],
  },
  {
    // Extensão de Contas a Pagar: fornecedor, observação e itens de produtos/insumos.
    version: 27,
    statements: [
      `ALTER TABLE conta ADD COLUMN fornecedor TEXT`,
      `ALTER TABLE conta ADD COLUMN observacao TEXT NOT NULL DEFAULT ''`,
      `ALTER TABLE conta ADD COLUMN itens TEXT NOT NULL DEFAULT '[]'`,
    ],
  },
  {
    // Produtos/Insumos ganham Marca; a identidade passa a ser (nome, marca) em
    // vez de só nome (issue #67). `estoque_minimo` continua na tabela, intocado
    // — só sai do formulário de cadastro (ADR-0017).
    version: 28,
    statements: [`ALTER TABLE insumo ADD COLUMN marca TEXT`],
  },
  {
    // Backlog Inicial (issue #68, ADR-0019): saldo pré-sistema por
    // Cliente/Tipo, sem Lote de origem. `baixa_backlog_inicial` é paralela a
    // `baixa_backlog`, mas chaveada por Cliente em vez de Lote de origem — o
    // Backlog Inicial não tem Lote por trás, e é o único saldo que pode
    // ficar negativo.
    version: 29,
    statements: [
      `CREATE TABLE backlog_inicial (
        cliente_id TEXT NOT NULL REFERENCES cliente(id),
        tipo_id    TEXT NOT NULL REFERENCES tipo_peca(id),
        quantidade INTEGER NOT NULL,
        PRIMARY KEY (cliente_id, tipo_id)
      )`,
      `CREATE TABLE baixa_backlog_inicial (
        lote_id    TEXT NOT NULL REFERENCES lote(id),
        cliente_id TEXT NOT NULL REFERENCES cliente(id),
        tipo_id    TEXT NOT NULL REFERENCES tipo_peca(id),
        quantidade INTEGER NOT NULL,
        PRIMARY KEY (lote_id, cliente_id, tipo_id)
      )`,
    ],
  },
  {
    // Contagem Conferida (issue #70, ADR-0020, redefinida no ADR-0021): o que
    // a lavanderia contou de fato, só pro cálculo do excedente/Backlog — a
    // Recebida em si nunca muda. Aditiva, ambas nulas por padrão (sem
    // divergência registrada).
    version: 30,
    statements: [
      `ALTER TABLE lote_contagem ADD COLUMN conferida INTEGER`,
      `ALTER TABLE lote_contagem ADD COLUMN conferida_motivo TEXT`,
    ],
  },
  {
    // Perda (Fase 5, ADR-0022): saída definitiva do Backlog — Peça extraviada
    // ou danificada, que não volta ao Cliente. `origem_lote_id` nulo = a Perda
    // saiu do Backlog Inicial, que não tem Lote por trás.
    version: 31,
    statements: [
      `CREATE TABLE perda (
        id             TEXT PRIMARY KEY,
        cliente_id     TEXT NOT NULL REFERENCES cliente(id),
        tipo_id        TEXT NOT NULL REFERENCES tipo_peca(id),
        origem_lote_id TEXT REFERENCES lote(id),
        motivo_origem  TEXT NOT NULL,
        motivo         TEXT NOT NULL,
        quantidade     INTEGER NOT NULL,
        data           TEXT NOT NULL
      )`,
    ],
  },
  {
    // Retida na Comanda (ADR-0023): quantidade declarada pelo cliente como
    // devolvida não-finalizada (fluxo Lançar OS -> fechamento).
    version: 32,
    statements: [
      `ALTER TABLE lote_contagem ADD COLUMN retida_informada INTEGER`,
    ],
  },
  {
    // OS "Reaberta" (ADR-0024): marca quando a Fatura que continha esta OS
    // foi reaberta, pra situacaoLote parar de cair direto em "fechado".
    version: 33,
    statements: [
      `ALTER TABLE lote ADD COLUMN reaberto_em TEXT`,
    ],
  },
  {
    // Permissões reais por Perfil (issue #71): Perfil ganha `ativo`, no mesmo
    // padrão de Categoria/Funcionário/Insumo — some da lista de opções sem
    // apagar o histórico de quem já teve o Perfil.
    version: 34,
    statements: [
      `ALTER TABLE perfil ADD COLUMN ativo INTEGER NOT NULL DEFAULT 1`,
    ],
  },
  {
    // Reabrir o Fechamento de OS (2026-09-28): a OS volta a Lançada até o
    // próximo "Fechar OS"; a data/hora do fechamento que foi reaberto ficam
    // guardadas aqui (e `devolvido_em` vazio) para a Ficha reabrir com elas.
    version: 35,
    statements: [
      `ALTER TABLE lote ADD COLUMN fechamento_anterior_em TEXT`,
      `ALTER TABLE lote ADD COLUMN fechamento_anterior_hora TEXT`,
    ],
  },
  {
    // Carona retida de novo (ADR-0025): a baixa do Backlog diz se a Peça
    // voltou ao Cliente ("devolvida", tudo o que existia até aqui) ou ficou
    // retida de novo na OS ("retida"). O destino entra na chave primária —
    // SQLite não altera chave, então as duas tabelas são recriadas.
    version: 36,
    statements: [
      `CREATE TABLE baixa_backlog_nova (
        lote_id        TEXT NOT NULL REFERENCES lote(id),
        origem_lote_id TEXT NOT NULL REFERENCES lote(id),
        tipo_id        TEXT NOT NULL REFERENCES tipo_peca(id),
        motivo         TEXT NOT NULL,
        destino        TEXT NOT NULL DEFAULT 'devolvida',
        quantidade     INTEGER NOT NULL,
        PRIMARY KEY (lote_id, origem_lote_id, tipo_id, motivo, destino)
      )`,
      `INSERT INTO baixa_backlog_nova (lote_id, origem_lote_id, tipo_id, motivo, destino, quantidade)
        SELECT lote_id, origem_lote_id, tipo_id, motivo, 'devolvida', quantidade FROM baixa_backlog`,
      `DROP TABLE baixa_backlog`,
      `ALTER TABLE baixa_backlog_nova RENAME TO baixa_backlog`,
      `CREATE TABLE baixa_backlog_inicial_nova (
        lote_id    TEXT NOT NULL REFERENCES lote(id),
        cliente_id TEXT NOT NULL REFERENCES cliente(id),
        tipo_id    TEXT NOT NULL REFERENCES tipo_peca(id),
        destino    TEXT NOT NULL DEFAULT 'devolvida',
        quantidade INTEGER NOT NULL,
        PRIMARY KEY (lote_id, cliente_id, tipo_id, destino)
      )`,
      `INSERT INTO baixa_backlog_inicial_nova (lote_id, cliente_id, tipo_id, destino, quantidade)
        SELECT lote_id, cliente_id, tipo_id, 'devolvida', quantidade FROM baixa_backlog_inicial`,
      `DROP TABLE baixa_backlog_inicial`,
      `ALTER TABLE baixa_backlog_inicial_nova RENAME TO baixa_backlog_inicial`,
    ],
  },
  {
    // Ordem do Backlog (ADR-0028): OS devolvidas na mesma data e hora são
    // ordenadas pelo momento em que o Fechamento foi registrado no sistema.
    // Dado anterior fica sem (NULL) e vem antes no empate.
    version: 37,
    statements: [`ALTER TABLE lote ADD COLUMN fechamento_registrado_em TEXT`],
  },
  {
    // Rascunho do Fechamento (2026-09-30): o "Salvar" de uma OS lançada
    // guarda o que foi lançado no Fechamento — antes ficava só na memória da
    // página e as retidas classificadas sumiam ao recarregar. JSON; NULL sem
    // rascunho.
    version: 38,
    statements: [`ALTER TABLE lote ADD COLUMN rascunho_fechamento TEXT`],
  },
  {
    // ADR-0031: Compensação de Peças na Fatura — congelada junto na emissão
    version: 39,
    statements: [`ALTER TABLE fatura ADD COLUMN compensacoes TEXT NOT NULL DEFAULT '[]'`],
  },
  {
    // #87: a Conta a Pagar guarda o avulso; o total passa a ser avulso + itens
    // calculado na gravação. As existentes ganham avulso = total − itens (a
    // mesma conta da tela: quantidade × unitário arredondado, item a item).
    // Conferido na cópia de produção de 2026-10-01: nenhum avulso negativo.
    version: 40,
    statements: [
      `ALTER TABLE conta ADD COLUMN avulso_centavos INTEGER`,
      // avulso zero numa conta com itens vira nulo ("só os itens") — a tela já
      // mostrava esse campo vazio
      `UPDATE conta SET avulso_centavos = valor_centavos - COALESCE((
         SELECT CAST(SUM(ROUND(json_extract(value, '$.quantidade') * json_extract(value, '$.valorUnitarioCentavos'))) AS INTEGER)
         FROM json_each(conta.itens)
       ), 0)
       WHERE direcao = 'pagar'`,
      `UPDATE conta SET avulso_centavos = NULL
       WHERE direcao = 'pagar' AND avulso_centavos = 0 AND json_array_length(conta.itens) > 0`,
    ],
  },
];

/** Módulos do sistema, alvo das Permissões de Módulo. */
export const MODULOS = [
  "operacional",
  "financeiro",
  "estoque",
  "configuracao",
] as const;
export type Modulo = (typeof MODULOS)[number];

/** Níveis de acesso de um Perfil a um Módulo. */
export const NIVEIS_PERMISSAO = [
  "nenhum",
  "leitura",
  "gravacao",
  "ambos",
] as const;
export type NivelPermissao = (typeof NIVEIS_PERMISSAO)[number];

/**
 * `nivel` dá gravação? A regra em si — usada por `_acesso.ts` (checagem de
 * verdade), `comandos/perfil.ts` (guarda de auto-lockout) e `domain/seed.ts`
 * (achar um Perfil administrativo para usuário órfão). Um lugar só: a
 * mesma checagem de autorização reescrita em cada arquivo é como ela
 * apodrece sem ninguém notar.
 */
export function concedeGravacao(nivel: NivelPermissao): boolean {
  return nivel === "gravacao" || nivel === "ambos";
}

/** A maior versão de schema conhecida por este build. */
export function latestVersion(): number {
  return migrations.reduce((max, m) => Math.max(max, m.version), 0);
}
